import { inject, injectable } from 'inversify'
import { computed, makeObservable, observable, runInAction } from 'mobx'
import { searchHitSchema, type SearchHit } from '@shared/index'
import { HttpGateway } from '@core/services/http.gateway'
import { Types } from '@core/ioc/types'
import type { UserScopedState } from '@core/interfaces/user-scoped-state'
import type { CommandResult, CommandSource, CommandSourceFailurePm } from '@core/interfaces/command-source'
import type { RouteId } from '@core/routing/router.config'
import { z } from '@core/validation/validation.schema'
import { OperationRepository } from '@core/operations/operation.repository'
import type { OperationHandle, OperationOwner, ReadonlyOperationState } from '@core/operations/operation.types'
import {
  defineOperation,
  operationFailed,
  operationGroup,
  operationId,
  operationKey,
  operationSucceeded,
} from '@core/operations/operation.helpers'

export type SearchHitPm = {
  readonly type: SearchHit['type']
  readonly id: string
  readonly label: string
  readonly sublabel: string
}

const SearchResponseDtoSchema = z.object({ query: z.string(), hits: z.array(searchHitSchema) })

/**
 * A hit type with no detail route of its own yet routes to its list page
 * instead. `hit.type` is free-form (searchHitSchema), so this is a partial
 * map — a type the fixture features never index (nothing left to search
 * beyond Books) simply has no entry and its hit is dropped from `results`.
 */
const ROUTE_FOR_TYPE: Partial<Record<SearchHit['type'], RouteId>> = {
  book: 'Books',
}

// Constant key, not one per query: every keystroke's search is the same
// logical lane, which is what lets `pending: 'replace'` supersede the
// previous attempt instead of joining or queuing it (spec §14.1's coarse-key
// pattern, applied to a debounced read instead of a coarse write).
export const SEARCH_OPERATION = defineOperation<string, CommandSourceFailurePm>({
  id: operationId('search.query'),
  key: () => operationKey('search'),
  groups: () => [operationGroup('session'), operationGroup('search')],
  pending: { kind: 'replace' },
  safety: { kind: 'read' },
  // The command palette's own concrete demand for Phase 3 debounce (spec §21)
  // — nothing else in the app uses this policy yet.
  debounce: { kind: 'trailing', milliseconds: 250 },
  timeout: { kind: 'after', milliseconds: 10_000, toFailure: () => ({ code: 'temporarily-unavailable' }) },
  retry: { kind: 'none' },
})

/**
 * Backend-backed command source: `GET /search`, debounced. `searchHitsPm`
 * stays `null` until the first real search runs (CI-9 — a short/empty query
 * is genuinely "no search yet", not "searched and found nothing"); a query
 * under two characters clears it locally without ever reaching the network,
 * same threshold the backend itself applies.
 */
@injectable()
export class SearchRepository implements CommandSource, UserScopedState {
  readonly sourceId = 'search'
  readonly groupLabelKey = 'commandPalette.searchResults'

  searchHitsPm: readonly SearchHitPm[] | null = null

  private readonly dataGateway: HttpGateway
  private readonly operationOwner: OperationOwner
  private readonly searchOperation: OperationHandle<string, CommandSourceFailurePm>

  constructor(
    @inject(Types.IDataGateway) dataGateway: HttpGateway,
    @inject(OperationRepository) operationRepository: OperationRepository,
  ) {
    this.dataGateway = dataGateway
    this.operationOwner = operationRepository.createOwner({ id: 'search.repository', lifetime: 'session' })
    this.searchOperation = this.operationOwner.register(SEARCH_OPERATION)

    makeObservable(this, {
      searchHitsPm: observable.ref,
      results: computed,
    })
  }

  get state(): ReadonlyOperationState<CommandSourceFailurePm> {
    return this.searchOperation.stateFor('search')
  }

  get results(): readonly CommandResult[] {
    return (this.searchHitsPm ?? [])
      .map((hit): CommandResult | null => {
        const routeId = ROUTE_FOR_TYPE[hit.type]
        if (!routeId) return null
        const label = { kind: 'literal' as const, text: hit.sublabel ? `${hit.label} — ${hit.sublabel}` : hit.label }
        return {
          kind: 'navigate',
          key: `${hit.type}:${hit.id}`,
          label,
          routeId,
        }
      })
      .filter((result): result is CommandResult => result !== null)
  }

  onQueryChanged = (query: string): void => {
    const trimmed = query.trim()
    if (trimmed.length < 2) {
      this.searchOperation.reset('search', { kind: 'manual' })
      runInAction(() => {
        this.searchHitsPm = null
      })
      return
    }
    void this.search(trimmed)
  }

  private search = async (query: string): Promise<void> => {
    await this.searchOperation.run(
      query,
      async ({ signal }) => {
        const response = await this.dataGateway.get(`/search?q=${encodeURIComponent(query)}`, {
          signal,
          idempotencyKey: null,
        })
        if (!response.success) return operationFailed({ code: 'temporarily-unavailable' })

        const parsed = SearchResponseDtoSchema.safeParse(response.result)
        if (!parsed.success) return operationFailed({ code: 'temporarily-unavailable' })

        return operationSucceeded(
          Object.freeze(
            parsed.data.hits.map((hit): SearchHitPm =>
              Object.freeze({ type: hit.type, id: hit.id, label: hit.label, sublabel: hit.sublabel }),
            ),
          ),
        )
      },
      {
        succeeded: (hits) => {
          this.searchHitsPm = hits
        },
        // A failed search just shows no results rather than stale ones from a
        // previous, different query — `state` still carries the error for a
        // surface that wants to say so.
        failed: () => {
          this.searchHitsPm = []
        },
      },
    )
  }

  resetUserScopedState = (): void => {
    this.operationOwner.resetAll({ kind: 'session-reset' })
    runInAction(() => {
      this.searchHitsPm = null
    })
  }
}
